import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Sliders, Trash2, ExternalLink, Calendar } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { StoreItem } from '@/types';

interface StoreCardProps {
  store: StoreItem;
  onDisconnect: (store: StoreItem) => void;
}

export const StoreCard: React.FC<StoreCardProps> = ({ store, onDisconnect }) => {
  const navigate = useNavigate();
  const { currentRole } = useWorkspaceStore();
  const canManageStores = currentRole === 'Owner' || currentRole === 'Admin';

  const getProviderConfig = (provider: string) => {
    const normalized = provider.toLowerCase();
    switch (normalized) {
      case 'shopify':
        return {
          label: 'Shopify',
          colorClasses: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
          iconBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
          defaultDomain: 'myshopify.com',
        };
      case 'nuvemshop':
        return {
          label: 'Nuvemshop',
          colorClasses: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
          iconBg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
          defaultDomain: 'nuvemshop.com.br',
        };
      case 'woocommerce':
        return {
          label: 'WooCommerce',
          colorClasses: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
          iconBg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
          defaultDomain: 'woocommerce.store',
        };
      default:
        return {
          label: provider,
          colorClasses: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
          iconBg: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400',
          defaultDomain: 'custom.api',
        };
    }
  };

  const providerConfig = getProviderConfig(store.provider);
  const isConnected = store.status === 'Connected' || store.status === undefined;

  const formattedDate = store.connectedAt
    ? new Date(store.connectedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Recentemente';

  return (
    <Card className="flex flex-col justify-between transition-all duration-200 hover:shadow-md hover:border-primary/40 group overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={`h-10 w-10 shrink-0 rounded-xl ${providerConfig.iconBg} flex items-center justify-center font-bold shadow-sm transition-transform group-hover:scale-105`}
            >
              <Store className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base font-semibold text-foreground truncate">
                {store.name || store.storeName || 'Loja sem nome'}
              </CardTitle>
              <div className="flex items-center gap-2 mt-0.5 min-w-0">
                <Badge variant="outline" className={`shrink-0 text-[10px] px-1.5 py-0 h-4 ${providerConfig.colorClasses}`}>
                  {providerConfig.label}
                </Badge>
                <span className="text-[11px] text-muted-foreground font-mono truncate">
                  {store.provider === 'shopify'
                    ? `${(store.name || store.storeName || 'store').toLowerCase().replace(/\s+/g, '-')}.${providerConfig.defaultDomain}`
                    : store.provider === 'nuvemshop'
                    ? `${(store.name || store.storeName || 'store').toLowerCase().replace(/\s+/g, '')}.${providerConfig.defaultDomain}`
                    : providerConfig.defaultDomain}
                </span>
              </div>
            </div>
          </div>

          <Badge
            variant={isConnected ? 'default' : 'secondary'}
            className={`shrink-0 ${
              isConnected
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-xs font-medium'
                : 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20 text-xs'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-400'
              }`}
            />
            {isConnected ? 'Conectada' : store.status || 'Desconectada'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5 text-xs text-muted-foreground py-2.5 border-y border-border/40 bg-muted/20">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground/70" />
            Conectada:
          </span>
          <span className="text-foreground font-medium">{formattedDate}</span>
        </div>
      </CardContent>

      <CardFooter className="pt-3 gap-2 flex items-center justify-between">
        <Button
          variant="default"
          size="sm"
          className="flex-1 gap-1.5 text-xs h-8"
          onClick={() => navigate(`/integrations?storeId=${store.id}`)}
        >
          <Sliders className="h-3.5 w-3.5" />
          <span>Configurar Integração</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs h-8 text-muted-foreground hover:text-foreground"
          onClick={() => {
            const previewUrl = `/conversations?storeId=${store.id}`;
            navigate(previewUrl);
          }}
        >
          <span>Conversas</span>
          <ExternalLink className="h-3 w-3" />
        </Button>

        {canManageStores && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onDisconnect(store)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-[10px]">Desconectar Loja</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardFooter>
    </Card>
  );
};
